import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../src/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Video, Calendar, User, Filter } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AdvancedSchedulePicker } from '@/components/AdvancedSchedulePicker';

interface Activity {
  id: string;
  title: string;
  description?: string;
  activity_type: string;
  zoom_link?: string;
  meeting_id?: string;
  passcode?: string;
  facilitator_id?: string;
  facilitators?: { name: string; role: string }[];
  region_id?: string;
  schedule?: string;
  timezone?: string;
  schedule_type?: string;
  start_date?: string;
  start_time?: string;
  end_time?: string;
  has_zoom_meeting?: boolean;
  is_active: boolean;
  members?: { full_name: string };
  regions?: { name: string };
}

interface Member {
  id: string;
  full_name: string;
}

interface Facilitator {
  id: string;
  full_name: string;
}

interface Region {
  id: string;
  name: string;
}

interface ActivityManagerProps {
  userRole: string;
  userRegion?: string;
}

export const ActivityManager: React.FC<ActivityManagerProps> = ({ userRole, userRegion }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [facilitators, setFacilitators] = useState<Facilitator[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterRegion, setFilterRegion] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [facilitatorNames, setFacilitatorNames] = useState('');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    activity_type: '',
    zoom_link: '',
    meeting_id: '',
    passcode: '',
    facilitator_id: '',
    facilitators: [] as { name: string; role: string }[],
    region_id: '',
    schedule: '',
    timezone: 'EST',
    schedule_type: 'weekly',
    start_date: '',
    start_time: '',
    end_time: '',
    has_zoom_meeting: false,
    is_active: true
  });

  const [scheduleData, setScheduleData] = useState({
    scheduleType: 'weekly',
    startTime: '19:00',
    endTime: '20:00',
    timezone: 'EST'
  });

  const activityTypes = [
    'prayer_meeting',
    'womens_ministry',
    'bible_study',
    'leadership_meeting',
    'regional_meeting',
    'youth_ministry',
    'fellowship'
  ];

  useEffect(() => {
    fetchActivities();
    fetchMembers();
    fetchFacilitators();
    fetchRegions();
  }, [filterType, filterRegion, filterStatus, userRole, userRegion]);

  const fetchActivities = async () => {
    try {
      let query = supabase
        .from('activities')
        .select('*, members(full_name), regions(name)')
        .order('created_at', { ascending: false });

      // Apply role-based filtering
      if (userRole === 'regional_admin' && userRegion) {
        query = query.eq('region_id', userRegion);
      }

      if (filterType !== 'all') {
        query = query.eq('activity_type', filterType);
      }

      if (filterRegion !== 'all') {
        query = query.eq('region_id', filterRegion);
      }

      if (filterStatus !== 'all') {
        query = query.eq('is_active', filterStatus === 'active');
      }

      const { data, error } = await query;

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast({
        title: "Error",
        description: "Failed to fetch activities",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      let query = supabase
        .from('members')
        .select('id, full_name')
        .is('end_year', null)
        .eq('is_active', true);

      if (userRole === 'regional_admin' && userRegion) {
        query = query.eq('region_id', userRegion);
      }

      const { data, error } = await query;

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchFacilitators = async () => {
    try {
      let query = supabase
        .from('facilitators')
        .select('id, full_name')
        .order('full_name');

      if (userRole === 'regional_admin' && userRegion) {
        query = query.eq('region_id', userRegion);
      }

      const { data, error } = await query;

      if (error) throw error;
      setFacilitators(data || []);
    } catch (error) {
      console.error('Error fetching facilitators:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const { data, error } = await supabase
        .from('regions')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setRegions(data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Store facilitator names as text in the facilitator_id field
      const cleanedData = {
        ...formData,
        facilitator_id: facilitatorNames.trim() || null,
        region_id: formData.region_id && formData.region_id !== 'all' ? formData.region_id : null,
        start_time: formData.start_time?.trim() || null,
        end_time: formData.end_time?.trim() || null,
        start_date: formData.start_date?.trim() || null
      };

      if (editingActivity) {
        const { error } = await supabase
          .from('activities')
          .update(cleanedData)
          .eq('id', editingActivity.id);

        if (error) throw error;
        toast({ title: "Success", description: "Activity updated successfully" });
      } else {
        const { error } = await supabase
          .from('activities')
          .insert([cleanedData]);

        if (error) throw error;
        toast({ title: "Success", description: "Activity created successfully" });
      }

      resetForm();
      fetchActivities();
    } catch (error) {
      console.error('Error saving activity:', error);
      toast({
        title: "Error",
        description: "Failed to save activity",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) return;

    try {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Success", description: "Activity deleted successfully" });
      fetchActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast({
        title: "Error",
        description: "Failed to delete activity",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      activity_type: '',
      zoom_link: '',
      meeting_id: '',
      passcode: '',
      facilitator_id: '',
      region_id: userRole === 'regional_admin' ? userRegion || '' : '',
      schedule: '',
      timezone: 'EST',
      schedule_type: 'weekly',
      start_date: '',
      start_time: '',
      end_time: '',
      has_zoom_meeting: false,
      is_active: true
    });
    setScheduleData({
      scheduleType: 'weekly',
      startTime: '19:00',
      endTime: '20:00',
      timezone: 'EST'
    });
    setEditingActivity(null);
    setIsDialogOpen(false);
    setFacilitatorNames('');
  };

  const openEditDialog = (activity: Activity) => {
    setEditingActivity(activity);
    setFormData({
      title: activity.title,
      description: activity.description || '',
      activity_type: activity.activity_type,
      zoom_link: activity.zoom_link || '',
      meeting_id: activity.meeting_id || '',
      passcode: activity.passcode || '',
      facilitator_id: activity.facilitator_id || '',
      facilitators: activity.facilitators || [],
      region_id: activity.region_id || '',
      schedule: activity.schedule || '',
      timezone: activity.timezone || 'EST',
      schedule_type: activity.schedule_type || 'weekly',
      start_date: activity.start_date || '',
      start_time: activity.start_time || '',
      end_time: activity.end_time || '',
      has_zoom_meeting: activity.has_zoom_meeting || false,
      is_active: activity.is_active
    });
    setFacilitatorNames(activity.facilitator_id || '');
    setScheduleData({
      scheduleType: activity.schedule_type || 'weekly',
      startTime: activity.start_time || '19:00',
      endTime: activity.end_time || '20:00',
      timezone: activity.timezone || 'EST',
      customSchedule: activity.schedule || '',
      ...(activity.start_date && { startDate: new Date(activity.start_date) })
    });
    setIsDialogOpen(true);
  };

  const formatActivityType = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const addCustomFacilitator = async () => {
    if (!newFacilitator.name.trim()) return;
    
    try {
      const facilitatorData = {
        full_name: newFacilitator.name.trim(),
        email: newFacilitator.email.trim() || null,
        region_id: userRole === 'regional_admin' ? userRegion : formData.region_id || null
      };

      const { data, error } = await supabase
        .from('facilitators')
        .insert([facilitatorData])
        .select()
        .single();
      
      if (error) throw error;
      
      await fetchFacilitators();
      setFormData({ ...formData, facilitator_id: data.id });
      setNewFacilitator({ name: '', email: '' });
      setShowFacilitatorInput(false);
      
      toast({ title: "Success", description: "Custom facilitator added successfully" });
    } catch (error) {
      console.error('Error adding custom facilitator:', error);
      toast({
        title: "Error",
        description: "Failed to add custom facilitator",
        variant: "destructive"
      });
    }
  };

  const canManageActivities = userRole === 'global_admin' || userRole === 'Global Admin' || userRole === 'regional_admin' || userRole === 'Regional Admin';

  // Debug logging
  console.log('ActivityManager Debug:', {
    userRole,
    userRegion,
    canManageActivities,
    activitiesCount: activities.length
  });

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading activities...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Activities</h2>
          {canManageActivities && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Activity
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingActivity ? 'Edit Activity' : 'Add New Activity'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="activity_type">Activity Type</Label>
                    <Select value={formData.activity_type} onValueChange={(value) => setFormData({ ...formData, activity_type: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select activity type" />
                      </SelectTrigger>
                      <SelectContent>
                        {activityTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {formatActivityType(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="has_zoom_meeting"
                      checked={formData.has_zoom_meeting}
                      onCheckedChange={(checked) => setFormData({ ...formData, has_zoom_meeting: checked })}
                    />
                    <Label htmlFor="has_zoom_meeting">This is a Zoom meeting</Label>
                  </div>

                  {formData.has_zoom_meeting && (
                    <>
                      <div>
                        <Label htmlFor="zoom_link">Zoom Link</Label>
                        <Input
                          id="zoom_link"
                          type="url"
                          value={formData.zoom_link}
                          onChange={(e) => setFormData({ ...formData, zoom_link: e.target.value })}
                          placeholder="https://zoom.us/j/..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="meeting_id">Meeting ID</Label>
                          <Input
                            id="meeting_id"
                            value={formData.meeting_id}
                            onChange={(e) => setFormData({ ...formData, meeting_id: e.target.value })}
                            placeholder="123 456 7890"
                          />
                        </div>
                        <div>
                          <Label htmlFor="passcode">Passcode</Label>
                          <Input
                            id="passcode"
                            value={formData.passcode}
                            onChange={(e) => setFormData({ ...formData, passcode: e.target.value })}
                            placeholder="Meeting passcode"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Facilitators</Label>
                    <div className="space-y-3">
                      {(formData.facilitators || []).map((facilitator, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <Input
                            placeholder="Facilitator name"
                            value={facilitator?.name || ''}
                            onChange={(e) => {
                              const newFacilitators = [...(formData.facilitators || [])];
                              newFacilitators[index].name = e.target.value;
                              setFormData({ ...formData, facilitators: newFacilitators });
                            }}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Role (e.g., Speaker, Host)"
                            value={facilitator.role}
                            onChange={(e) => {
                              const newFacilitators = [...formData.facilitators];
                              newFacilitators[index].role = e.target.value;
                              setFormData({ ...formData, facilitators: newFacilitators });
                            }}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newFacilitators = formData.facilitators.filter((_, i) => i !== index);
                              setFormData({ ...formData, facilitators: newFacilitators });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            facilitators: [...formData.facilitators, { name: '', role: '' }]
                          });
                        }}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Facilitator
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="region_id">Region</Label>
                    <Select value={formData.region_id} onValueChange={(value) => setFormData({ ...formData, region_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {userRole === 'global_admin' && <SelectItem value="all">All regions</SelectItem>}
                        {regions.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Schedule Details</Label>
                    <AdvancedSchedulePicker
                      value={scheduleData}
                      onChange={(schedule) => {
                        setScheduleData(schedule);
                        setFormData({ 
                          ...formData, 
                          schedule_type: schedule.scheduleType,
                          start_time: schedule.startTime,
                          end_time: schedule.endTime,
                          timezone: schedule.timezone,
                          start_date: schedule.startDate ? schedule.startDate.toISOString().split('T')[0] : '',
                          schedule: schedule.customSchedule || ''
                        });
                      }}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active">Active</Label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingActivity ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {activityTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {formatActivityType(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(userRole === 'global_admin' || userRole === 'Global Admin') && (
            <Select value={filterRegion} onValueChange={setFilterRegion}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4">
        {activities.map((activity) => (
          <Card key={activity.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{activity.title}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">
                      {formatActivityType(activity.activity_type)}
                    </Badge>
                    {!activity.is_active && (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </div>
                </div>
                
                {canManageActivities && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(activity)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    {userRole === 'global_admin' && (
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(activity.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              {activity.description && (
                <p className="text-sm text-muted-foreground mb-3">{activity.description}</p>
              )}
              
              <div className="space-y-2">
                {activity.schedule && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{activity.schedule}</span>
                  </div>
                )}
                
                {activity.facilitators && activity.facilitators.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      <span>Facilitators:</span>
                    </div>
                    {(activity.facilitators || []).map((facilitator, index) => (
                      <div key={index} className="ml-6 text-sm text-muted-foreground">
                        {facilitator.name} - {facilitator.role}
                      </div>
                    ))}
                  </div>
                )}
                
                {activity.regions && (
                  <div className="flex items-center gap-2 text-sm">
                    <span>🌍 Region: {activity.regions.name}</span>
                  </div>
                )}
                
                {activity.has_zoom_meeting && activity.zoom_link && (
                  <div className="flex items-center gap-2 text-sm">
                    <Video className="h-4 w-4" />
                    <a 
                      href={activity.zoom_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Join Zoom Meeting
                    </a>
                  </div>
                )}
                
                {activity.meeting_id && (
                  <div className="text-sm">
                    <span className="font-medium">Meeting ID:</span> {activity.meeting_id}
                    {activity.passcode && (
                      <span className="ml-4">
                        <span className="font-medium">Passcode:</span> {activity.passcode}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {activities.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No activities found</p>
        </div>
      )}
    </div>
  );
};